import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Activity, ArrowRight } from 'lucide-react';
import { sumLog, calcTargets, inferNutritionGoal } from '../data/nutrition';
import { localISO } from '../data/programs';

/**
 * MacroPattern — last-7-day macro fingerprint for the Dashboard.
 *
 * Anti-shame design (per STANDING_INSTRUCTIONS §pattern over streak):
 *  - No streaks. No "broken" red. No green "perfect" checkmarks.
 *  - Just the visual: each day, was the macro within ±10% of target?
 *  - One-line summary per macro: "N of 7 on target" — not "you missed 2 days."
 *
 * Bar interpretation per day:
 *  - On target (≥90% AND ≤110% of target): brand red (faded)
 *  - Under (< 90%): muted zinc bar at relative height
 *  - Over (> 110%): amber bar at relative height (gentle warning, not alarm)
 *  - No data (no log entries that day): empty slot
 */
const MacroPattern = ({ profile }) => {
  const targets = useMemo(() => {
    if (!profile.weight || !profile.age || !profile.sex || !profile.heightFt) return null;
    const goalId = profile.nutritionGoal || inferNutritionGoal(profile.goals || []);
    return calcTargets({ profile, goalId, activityId: profile.activityLevel || 'moderate' });
  }, [profile]);

  // Build 7-day series: oldest left, today right
  const days = useMemo(() => {
    const out = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today.getTime() - i * 86400000);
      const iso = localISO(d);
      const entries = (profile.foodLog && profile.foodLog[iso]) || [];
      const totals = entries.length > 0 ? sumLog(entries) : null;
      out.push({
        iso,
        label: d.toLocaleDateString('en-US', { weekday: 'narrow' }),
        fullLabel: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        hasData: entries.length > 0,
        totals,
      });
    }
    return out;
  }, [profile.foodLog]);

  // Don't render at all without targets — Today's Fuel tile already nags
  if (!targets) return null;

  // Macro definitions for the 4 sub-charts.
  const macros = [
    { key: 'kcal',    label: 'Calories', target: targets.calories, unit: '',   color: 'enf-accent' },
    { key: 'protein', label: 'Protein',  target: targets.proteinG, unit: 'g',  color: 'enf-accent' },
    { key: 'carbs',   label: 'Carbs',    target: targets.carbG,    unit: 'g',  color: 'enf-role-info' },
    { key: 'fat',     label: 'Fat',      target: targets.fatG,     unit: 'g',  color: 'amber-500' },
  ];

  // Classify a day's value vs target. Returns { status, pct } where pct is
  // the bar height fraction (0..1.2). status drives color.
  const classify = (val, target) => {
    if (val == null) return { status: 'none', pct: 0 };
    const ratio = target > 0 ? val / target : 0;
    if (ratio >= 0.9 && ratio <= 1.1) return { status: 'on', pct: Math.min(1.0, ratio) };
    if (ratio < 0.9) return { status: 'under', pct: Math.max(0.05, ratio) };
    return { status: 'over', pct: Math.min(1.2, ratio) };
  };

  // Status → tailwind class. Encapsulated so palette tweaks land in one place.
  const barClass = (status) => {
    if (status === 'on')    return 'bg-enf-accent/80';
    if (status === 'under') return 'bg-enf-surface-elevated';
    if (status === 'over')  return 'bg-amber-500/80';
    return 'bg-enf-surface-mid';
  };

  const daysWithData = days.filter((d) => d.hasData).length;

  return (
    <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6" data-testid="macro-pattern-card">
      <div className="flex items-start justify-between mb-5 gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-enf-surface-mid flex items-center justify-center flex-shrink-0">
            <Activity className="w-5 h-5 text-enf-accent-fg" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-enf-role-info font-semibold mb-1">Pattern · last 7 days</div>
            <div className="text-enf-text-secondary text-sm">
              {daysWithData === 0
                ? 'No food logged in the last 7 days yet.'
                : daysWithData === 7
                ? 'A week of data. Look at the shape, not the streak.'
                : `${daysWithData} of 7 days logged. Pattern emerges as you fill it in.`}
            </div>
          </div>
        </div>
        <Link to="/nutrition" className="text-enf-role-info hover:text-enf-text-primary text-xs flex items-center gap-1 transition-colors flex-shrink-0">
          Log food <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      {/* Four mini-charts, one per macro */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {macros.map((m) => {
          const dayValues = days.map((d) => {
            if (!d.hasData) return { ...d, val: null, status: 'none', pct: 0 };
            const val = d.totals[m.key];
            const cls = classify(val, m.target);
            return { ...d, val, ...cls };
          });
          const onTargetCount = dayValues.filter((d) => d.status === 'on').length;

          return (
            <div key={m.key}>
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-[10px] uppercase tracking-widest text-enf-text-secondary font-semibold">{m.label}</span>
                <span className="text-[10px] text-enf-text-muted">target {Math.round(m.target)}{m.unit}</span>
              </div>
              {/* Bar row — fixed height container, bars grow up from a baseline */}
              <div className="flex items-end gap-1 h-14 mb-2" data-testid={`macro-pattern-bars-${m.key}`}>
                {dayValues.map((d) => {
                  const heightPct = Math.round(d.pct * 100);
                  return (
                    <div key={d.iso} className="flex-1 flex flex-col justify-end h-full" title={d.hasData ? `${d.fullLabel}: ${Math.round(d.val)}${m.unit} (${Math.round((d.val / m.target) * 100)}% of target)` : `${d.fullLabel}: no data`}>
                      <div
                        className={`${barClass(d.status)} rounded-sm transition-all duration-500`}
                        style={{ height: `${Math.max(d.status === 'none' ? 6 : 12, Math.min(100, heightPct))}%` }}
                      />
                    </div>
                  );
                })}
              </div>
              {/* Day labels under bars (narrow weekday letters) */}
              <div className="flex items-center gap-1 mb-2">
                {dayValues.map((d, i) => (
                  <span key={i} className="flex-1 text-center text-[9px] text-enf-text-muted">{d.label}</span>
                ))}
              </div>
              <div className="text-xs text-enf-text-secondary">
                <span className="font-semibold text-enf-accent-fg/90">{onTargetCount}</span><span className="text-enf-text-muted"> of {daysWithData || 7} on target</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend — small, single line, no judgment language */}
      <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-enf-text-muted">
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-enf-accent/80" /> within ±10%</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-enf-surface-elevated" /> under</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-amber-500/80" /> over</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-enf-surface-mid" /> no data</span>
      </div>
    </div>
  );
};

export default MacroPattern;
